// Tracks the selection writes in flight across the open benefit group's lines, so the page can let
// them settle before it sends a group or all Deselect. A PUT still on its way, or the queued
// follow-up a `useRbpSelection` flush sends when the first settles, must not reach the server after
// the DELETE and re-create a cleared selection. Deselect waits for `settled()` before the DELETE,
// so the clear is always the last write for those lines. Unmounting the group drops only the queued
// write, not an already in-flight one, so it cannot order the clear after a settled request.
//
// Settling drains what is already on its way, but a shopper can still step a line in the same tick
// the Deselect fires, or while it awaits the settle and the DELETE, and start a fresh PUT that
// lands after the DELETE. So Deselect also latches `beginClear()` synchronously before it awaits:
// `isClearing()` is read by each step (a synchronous ref, not a render) and a step started while a
// clear is latched does nothing, so no new write can begin until `endClear()` releases it. The
// latch also drives the steppers' disabled state through `subscribe`.

export interface RbpPendingSelections {
  /** Track one line's whole flush run: the in-flight write and the queue it drains. */
  track(run: Promise<unknown>): void;
  /** Resolves once no tracked run is in flight. */
  settled(): Promise<void>;
  /** Latch a clear synchronously: from now no step may start a write until `endClear()`. */
  beginClear(): void;
  /** Release the clear latch, after the DELETE completes or fails. */
  endClear(): void;
  /** True while a clear is latched. Read synchronously by a step so a same-tick click is blocked. */
  isClearing(): boolean;
  /** Subscribe to clear-latch changes (drives the steppers' disabled state). Returns unsubscribe. */
  subscribe(listener: () => void): () => void;
}

export function createRbpPendingSelections(): RbpPendingSelections {
  const active = new Set<Promise<unknown>>();
  const listeners = new Set<() => void>();
  let clearing = false;
  const notify = () => {
    for (const listener of listeners) listener();
  };
  return {
    track(run) {
      active.add(run);
      void Promise.resolve(run)
        .catch(() => undefined)
        .finally(() => active.delete(run));
    },
    async settled() {
      // A flush may queue a follow-up run while this awaits, so re-check until the set is empty.
      while (active.size > 0) {
        await Promise.allSettled([...active]);
      }
    },
    beginClear() {
      if (clearing) return;
      clearing = true;
      notify();
    },
    endClear() {
      if (!clearing) return;
      clearing = false;
      notify();
    },
    isClearing() {
      return clearing;
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
